import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { insertIf } from '../../../../helpers/common'
import { useConversionResultsColumns } from './ConversionResultsColumns/useConversionResultsColumns'

import { selectedPartnershipSelector } from '../../../../modules/selectors/partnerships'

import { DV_360_PLATFORM, GOOGLE_PLATFORM, TIKTOK_PLATFORM } from '../../../../constants/selectLists/platformList'
import { listItemsTableColumnsMinWidth } from '../columnsWidth'

import useStyles from '../styles'

export const useReusableInsightsColumns = ({ platform, items, useConversionResultsField }) => {
  const classes = useStyles()

  const selectedPartnership = useSelector(selectedPartnershipSelector)

  const { spend_metrics: showAdditionalMetrics, advanced_metrics: advancedMetrics } = selectedPartnership

  const currencySymbol = selectedPartnership?.facebook_ad_account?.currency_symbol

  // depending on platform we should dynamically hide or show columns
  const isGooglePlatform = platform === GOOGLE_PLATFORM
  const isDV360Platform = platform === DV_360_PLATFORM
  const isTiktokPlatform = platform === TIKTOK_PLATFORM

  const isItemWithVideoViewsExist = useMemo(() => items.find(item => item['video_views']), [items])

  const conversionRelatedColumns = useConversionResultsColumns({
    items,
    currencySymbol,
    showAdditionalMetrics,
    advancedMetrics,
    showAttributionWindow: true
  })

  return useMemo(() => {
    return [
      {
        headTitle: 'Impressions',
        Component: ({ impressions, cpm }) => (
          <>
            {impressions || '-'}
            {showAdditionalMetrics && <div className={classes.columnSubText}>CPM: {cpm || '-'}</div>}
          </>
        ),
        headClassName: classes.valueColumn,
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.impressions
        }
      },
      ...insertIf(isGooglePlatform, {
        headTitle: 'Viewable Impr.',
        Component: ({ active_view_impressions }) => active_view_impressions || '-',
        headClassName: classes.valueColumn,
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.activeViewImpressions
        }
      }),
      ...insertIf(!isGooglePlatform, {
        headTitle: 'Reach',
        Component: ({ reach, frequency }) => (
          <>
            {reach || '-'}
            <div className={classes.columnSubText}>Frequency {frequency || '-'}</div>
          </>
        ),
        headClassName: classes.valueColumn,
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.reach
        }
      }),
      {
        headTitle: 'Clicks',
        Component: ({ clicks, ctr }) => (
          <>
            {clicks || '-'}
            <div className={classes.columnSubText}>CTR: {ctr || '-'}</div>
          </>
        ),
        headClassName: classes.valueColumn,
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.clicks
        }
      },
      ...insertIf(!isGooglePlatform && !isDV360Platform && !isTiktokPlatform, {
        headTitle: 'Engagements',
        Component: ({ engagement }) => engagement || '-',
        headClassName: classes.valueColumn,
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.engagements
        }
      }),
      ...insertIf(isItemWithVideoViewsExist, {
        headTitle: 'Video Views',
        Component: ({ video_views, video_view_rate }) => (
          <>
            {video_views || '-'}
            {<div className={classes.columnSubText}>View Rate: {video_view_rate || '-'}</div>}
          </>
        ),
        headClassName: classes.valueColumn,
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.videoViews
        }
      }),

      ...conversionRelatedColumns,

      ...insertIf(showAdditionalMetrics, {
        headTitle: 'Spend',
        Component: ({ spend }) => {
          return spend || '-'
        },
        headClassName: classes.valueColumn,
        className: classes.valueColumn,
        style: {
          minWidth: listItemsTableColumnsMinWidth.spend
        }
      })
    ]
  }, [
    classes.columnSubText,
    classes.valueColumn,
    isGooglePlatform,
    isDV360Platform,
    isTiktokPlatform,
    conversionRelatedColumns,
    isItemWithVideoViewsExist,
    showAdditionalMetrics
  ])
}
